// logger.js — logger CENTRAL de offline-v2, pensado para DEBUG EN DEVICE (APK).
//
// Por qué existe: en un APK (`quasar build`, import.meta.env.DEV=false) los console.log gateados a DEV se
// eliminan, y un device real no siempre permite remote-inspect en builds release. Este logger:
//   - se ENCIENDE en runtime (no en compile-time) -> funciona DENTRO del APK.
//   - etiqueta todo `[offline-v2:<cat>]` (filtrable en chrome://inspect / Safari Web Inspector / logcat).
//   - mantiene un RING BUFFER en memoria recuperable con `window.__offlineV2Debug.dumpLog()` aunque la
//     consola no estuviera abierta en el momento del evento.
//   - los ERRORES SIEMPRE se registran (aunque el verbose esté apagado): nunca se pierde un fallo.
//
// Encender/apagar en device (persistente entre reloads):
//   localStorage.setItem('offline_v2_debug','1')   // '0' para apagar
//   // o en runtime: window.__offlineV2Debug.enableDebug() / .disableDebug()
//
// DEFAULT: APAGADO (release). El ring buffer puede contener filas completas de answers (PII
// potencial) y su API queda expuesta via window.__offlineV2Debug; con la capa offline en piloto,
// el debug se enciende explícitamente per-device: localStorage.setItem('offline_v2_debug','1') o
// window.__offlineV2Debug.enableDebug(). En dev (import.meta.env.DEV) sigue encendido para desarrollo.

const DEFAULT_ON = false
const RING_MAX = 1000
const STR_CAP = 200 // trunca strings largos (base64 de fotos) en el log

// Redacción por-LLAVE de PII [review Hugo #9]: STR_CAP trunca por longitud (evita el base64 gigante) pero
// NO protege PII — un nombre/correo/answer de texto corto mide < 200 chars y entraba íntegro al ring
// buffer (sale por window.__offlineV2Debug.dumpLog()). Se enmascara el VALOR de estas llaves en TODOS los
// callers de un solo lugar (_safe); el resto (id/uuid/status/timestamps/contadores/flags) se loguea íntegro.
// NOTA: NO se incluye el 'name' pelón — es una llave sobrecargada que el pull-engine usa como
// IDENTIFICADOR de catálogo (p.ej. {name:'tasks'}) en logs de debug legítimos; el nombre de persona se
// cubre con first_name/last_name/full_name/nombre. Así se enmascara PII sin cegar la señal de debug.
const REDACT_KEYS = new Set([
  'nombre', 'first_name', 'last_name', 'full_name', 'email', 'correo', 'phone', 'telefono',
  'answer', 'answers', 'value', 'valor', 'text', 'texto', 'comment', 'comentario',
  'signature', 'firma', 'address', 'direccion', 'rfc', 'curp', 'password'
])
const _sensitiveKey = (k) => REDACT_KEYS.has(String(k).toLowerCase())

let _ring = []
let _seq = 0

// ── PERSISTENCIA DEL RING (diagnóstico post-mortem) ─────────────────────────────────────────────
// El escenario que más se necesita diagnosticar es "la app MURIÓ (LMK/crash) y al reabrir algo se ve
// mal" — y un ring en memoria muere con la app: la evidencia de la captura/escritura previa se
// perdía. Se persiste la cola del ring (ya REDACTADA por _safe y capada por STR_CAP -> sin PII y
// acotada) a localStorage, con throttle para no pagar un write síncrono por entrada, y flush final en
// pagehide/visibilitychange (la última oportunidad antes de morir). Al arrancar, la sesión anterior
// se restaura al frente del ring con un marcador -> dumpLog()/el diagnóstico muestran QUÉ pasó antes
// del reinicio. Best-effort total: sin localStorage o con cuota llena, el logger sigue como antes.
const PERSIST_KEY = 'offline_v2_ring'
const PERSIST_MAX = 400 // entradas persistidas (redactadas): ~decenas de KB, no MB
const PERSIST_THROTTLE_MS = 2000
let _lastFlush = 0
let _flushTimer = null

function _ls () {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) return globalThis.localStorage
  } catch { /* noop */ }
  return null
}

function _flush () {
  try {
    const ls = _ls()
    if (ls) ls.setItem(PERSIST_KEY, JSON.stringify(_ring.slice(-PERSIST_MAX)))
  } catch { /* cuota/API ausente: no-op */ }
}

function _scheduleFlush () {
  if (_isTestEnv()) return
  const now = Date.now()
  if (now - _lastFlush >= PERSIST_THROTTLE_MS) { _lastFlush = now; _flush(); return }
  if (_flushTimer) return
  _flushTimer = setTimeout(() => { _flushTimer = null; _lastFlush = Date.now(); _flush() }, PERSIST_THROTTLE_MS)
}

// Restaura la sesión anterior al cargar el módulo (una vez por arranque).
function _restorePrevSession () {
  try {
    const ls = _ls()
    if (!ls) return
    const raw = ls.getItem(PERSIST_KEY)
    if (!raw) return
    const prev = JSON.parse(raw)
    if (Array.isArray(prev) && prev.length) {
      _ring = prev.slice(-PERSIST_MAX)
      _seq = Number(_ring[_ring.length - 1]?.seq) || _ring.length
      _ring.push({
        seq: ++_seq,
        t: _ts(),
        level: 'info',
        cat: 'lifecycle',
        msg: '── ↑ sesión ANTERIOR restaurada (la app se reinició) ↑ ──',
        data: { entradas: prev.length }
      })
    }
  } catch { /* payload corrupto: empezar limpio */ }
}
if (!_isTestEnv()) _restorePrevSession()

// Handle SOLO para tests (la persistencia real corre gateada por _isTestEnv): permite ejercer
// flush/restore contra un localStorage stub sin depender del timing del throttle.
export const __persistForTest = { flush: _flush, restore: _restorePrevSession, KEY: PERSIST_KEY, MAX: PERSIST_MAX }

// Último flush antes de morir/ocultarse (LMK suele llegar con la app ya en background).
try {
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('pagehide', _flush)
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') _flush() })
  }
} catch { /* noop */ }

/** ¿Estamos en el runner de tests? -> logger OFF (evita ensuciar/relentizar la suite). */
function _isTestEnv () {
  try {
    return !!(import.meta.env.VITEST || import.meta.env.MODE === 'test')
  } catch { return false }
}

/** Resuelve el estado ON/OFF: localStorage manda; luego el override de window; luego el default. */
function _resolveEnabled () {
  if (_isTestEnv()) return false
  try {
    const v = (typeof window !== 'undefined' && window.localStorage)
      ? window.localStorage.getItem('offline_v2_debug') : null
    if (v === '1') return true
    if (v === '0') return false
  } catch { /* localStorage no disponible (algunos WebView) */ }
  try {
    if (typeof window !== 'undefined' && typeof window.__offlineV2DebugForce === 'boolean') {
      return window.__offlineV2DebugForce
    }
  } catch { /* noop */ }
  let dev = false
  try { dev = !!import.meta.env.DEV } catch { /* noop */ }
  // Build de QA: la env OFFLINE_DEBUG=1 (inlineada en build, ver quasar.config.js) enciende el verbose
  // de fábrica en ese APK — QA no tiene consola para setear localStorage. El override local sigue
  // mandando en ambas direcciones (arriba). Prod se compila sin la env -> apagado como siempre.
  let qa = false
  try { qa = import.meta.env.OFFLINE_DEBUG === '1' } catch { /* noop */ }
  return DEFAULT_ON || dev || qa
}

let _enabled = _resolveEnabled()

function _ts () {
  try { return new Date().toISOString() } catch { return '' }
}

/** Aplana 1 nivel y trunca strings largos (no vuelca base64 gigante ni recursa ciclos). */
function _safe (data) {
  if (data == null) return data
  if (typeof data === 'string') return data.length > STR_CAP ? `«string ${data.length} chars»` : data
  if (typeof data !== 'object') return data
  const out = Array.isArray(data) ? [] : {}
  try {
    for (const k of Object.keys(data)) {
      const v = data[k]
      // Llave sensible: enmascara el valor (preservando null/undefined = señal "campo vacío") [Hugo #9].
      if (_sensitiveKey(k)) out[k] = (v == null) ? v : '«redacted»'
      else if (typeof v === 'string') out[k] = v.length > STR_CAP ? `«string ${v.length} chars»` : v
      else if (v && typeof v === 'object') out[k] = Array.isArray(v) ? `«array[${v.length}]»` : '«object»'
      else out[k] = v
    }
  } catch { return '«unserializable»' }
  return out
}

function _push (level, cat, msg, data) {
  _ring.push({ seq: ++_seq, t: _ts(), level, cat, msg, data: _safe(data) })
  if (_ring.length > RING_MAX) _ring.splice(0, _ring.length - RING_MAX)
  _scheduleFlush() // persistencia post-mortem (throttled, best-effort)
}

/** Log verbose (gateado). Para flujo normal: boot, saves, sync, fotos, lifecycle. */
export function dbg (cat, msg, data) {
  _push('debug', cat, msg, data)
  if (!_enabled) return
  const safe = _safe(data)
  if (safe === undefined) console.log(`[offline-v2:${cat}] ${msg}`)
  else console.log(`[offline-v2:${cat}] ${msg}`, safe)
}

/** Warning (gateado al verbose, pero siempre al ring). Para reintentos/throttle/skips. */
export function dbgWarn (cat, msg, data) {
  _push('warn', cat, msg, data)
  if (!_enabled) return
  const safe = _safe(data)
  if (safe === undefined) console.warn(`[offline-v2:${cat}] ${msg}`)
  else console.warn(`[offline-v2:${cat}] ${msg}`, safe)
}

// ── PUENTE AL VISOR DE LOGS DE LA APP (src/utils/logTracker) ────────────────────────────────────
// Este ring buffer solo se recupera por consola (window.__offlineV2Debug) y en un APK release NO hay
// consola: un fallo de la capa offline quedaba invisible dentro de la app aunque el usuario abriera
// el visor de logs (LogTracker, menu escondido), porque ese visor lee OTRO buffer distinto a este.
// Se espejan SOLO los errores (nivel error), nunca el verbose, para no ahogar el visor en ruido.
//
// El import es DINAMICO a proposito: evita cualquier ciclo de imports entre los dos loggers y no
// arrastra `vue` (dependencia de logTracker) a quien solo importe este modulo. La promesa se cachea
// para resolver el modulo una sola vez. Best-effort total: si el import falla, el logger sigue igual.
//
// PII: se manda EXACTAMENTE el mismo texto de error que entra al ring (stack o message), nunca datos
// del usuario, para conservar el criterio de redaccion de este logger en el otro buffer.
let _appLogSink = null

function _mirrorErrorToAppLog (cat, msg, detail) {
  try {
    if (_isTestEnv()) return
    if (!_appLogSink) _appLogSink = import('@/utils/logTracker').catch(() => null)
    _appLogSink
      .then((mod) => { if (mod && mod.log) mod.log.error(`[offline-v2:${cat}] ${msg}`, detail) })
      .catch(() => { /* noop */ })
  } catch { /* noop */ }
}

/** Error: SIEMPRE se registra (consola + ring), aunque el verbose esté apagado. Nunca se traga un fallo. */
export function dbgError (cat, msg, err) {
  const info = err && (err.stack || err.message) ? (err.stack || err.message) : err
  const detail = typeof info === 'string' ? info : String(info)
  _push('error', cat, msg, detail)
  _mirrorErrorToAppLog(cat, msg, detail)
  console.error(`[offline-v2:${cat}] ${msg}`, err)
}

// ─── API de recuperación del log (para device) ──────────────────────────────────────────────────

/** Copia del ring buffer (array de entradas). */
export function getLog () { return _ring.slice() }

/** Imprime el ring como tabla en consola y lo devuelve. Útil con chrome://inspect / Web Inspector. */
export function dumpLog () {
  const view = _ring.map(e => ({ seq: e.seq, t: e.t, level: e.level, cat: e.cat, msg: e.msg }))
  try { console.table(view) } catch { console.log(view) }
  return _ring.slice()
}

/** El log COMPLETO como texto plano (para copiar/compartir desde el device). */
export function dumpLogText () {
  return _ring.map(e => {
    let d = ''
    if (e.data !== undefined) { try { d = ' ' + JSON.stringify(e.data) } catch { d = ' «data»' } }
    return `${e.t} [${e.level}] ${e.cat}: ${e.msg}${d}`
  }).join('\n')
}

/** Intenta copiar el log de texto al portapapeles (si el WebView lo permite). */
export async function copyLog () {
  const text = dumpLogText()
  try { await navigator.clipboard.writeText(text); return 'log copiado al portapapeles' } catch { return text }
}

export function clearLog () { _ring = []; _seq = 0 }

export function enableDebug () {
  _enabled = true
  try { window.localStorage.setItem('offline_v2_debug', '1') } catch { /* noop */ }
  return 'offline-v2 debug: ON'
}

export function disableDebug () {
  _enabled = false
  try { window.localStorage.setItem('offline_v2_debug', '0') } catch { /* noop */ }
  return 'offline-v2 debug: OFF (los errores se siguen registrando)'
}

export function isDebugEnabled () { return _enabled }
